import React, {useState, useEffect, useRef} from "react"
import {Link} from "react-router-dom";
// MUI
import {Box, Button} from "@mui/material";
import {Add} from "@mui/icons-material";
// Data
import {useData} from "@/context/PackagingDataProvider";
// Components
import {ProductsDataTable} from "@/components/Products/index";
import ProductFilters from "@/components/Products/ProductFilters";
import {PageHeader} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";
// Hooks
import {useUser} from "@/context/UserProvider";
import {useTranslation} from "react-i18next";
import {useModule} from "@/context/ModuleProvider";
import {usePermissions} from "@/context/PermissionsProvider";
// Filtering functions
import {formatFiltersBody, getQueryUrl, initFilters} from "@/components/Products/filters/productFiltersManagement";

interface PageEvent { rows: number; page: number; first: number; }


export default function ProductsTab() {

    const {t} = useTranslation(["products", "common"])
    const tableInit = useRef(true)
    const {seeAnalyticsForProducts, userSettings, savePackagingFilters} = useUser()
    const {isAccountRole} = usePermissions()
    const {
        brands, getBrands,
        ranges, getRanges,
        products, getProducts,
        commonFilters, clearCommonFilters
    } = useData()
    const {modules} = useModule()

    const init = useRef(true)
    const filtersInit = useRef(true)

    const [preloading, setPreloading] = useState(false)
    const [loading, setLoading] = useState(true)
    const [dataTableEmptyMessage, setDataTableEmptyMessage] = useState<string>("common:loading")
    // Selection
    const [selection, setSelection] = useState<any>(null)
    const [multipleSelection, setMultipleSelection] = useState<any>(null)
    // Specific depth filter for ranges
    const [affectedRanges, setAffectedRanges] = useState<any>(null)
    // Other filters
    const [showFilters, setShowFilters] = useState<boolean>(true)
    const [filters, setFilters] = useState<any>(null)
    const [displayedDate, setDisplayedDate] = useState<Date | Date[] | null>(null)
    // Pagination
    const [dataTableFirst, setDataTableFirst] = useState<number>(0)
    // Table styling
    const [columns, setColumns] = useState<any>({
        filterDate: true,
        filterBrand: true,
        filterRange: true,
        filterType: false,
        filterDestination: false,
        filterStatus: false,
        filterShortUrl: true,
        filterProductShortUrl: false,
        filterMember: false,
    })

    let isFiltered = JSON.stringify(filters) !== JSON.stringify(initFilters)


    // Initiator
    useEffect(() => {
        if (!brands) getBrands()
        if (!ranges) getRanges()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Don't worry about that - if you remove it, things break
    useEffect(() => {
        if (tableInit.current) tableInit.current = false
    }, []);

    // Filters lifecycles
    useEffect(() => {
        if (!!userSettings.packaging_filter && filtersInit.current) {
            if (!!userSettings.packaging_filter && !!userSettings.packaging_filter.page) {
                // User has saved filters
                // Map saved filters
                hydrateFilters().then()
                filtersInit.current = false
            } else {
                setFilters(initFilters)
                filtersInit.current = false
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSettings?.packaging_filter]);

    const prevFiltersRef = useRef(undefined)
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        // First init cycle with simple boolean - emptying first-try
        if (init.current && !!filters) {
            init.current = false
            if (!prevFiltersRef.current) prevFiltersRef.current = filters
            refreshProducts()
            return
        }

        const prev: any = prevFiltersRef.current
        const curr: any = filters

        // Check if anything besides page changed
        const otherFiltersChanged =
            prev?.sortOrder !== curr?.sortOrder ||
            prev?.sortField !== curr?.sortField ||
            prev?.filterLabel !== curr?.filterLabel ||
            prev?.filterType !== curr?.filterType ||
            prev?.filterDestination !== curr?.filterDestination ||
            prev?.filterShortUrl !== curr?.filterShortUrl ||
            prev?.filterBrand !== curr?.filterBrand ||
            prev?.filterRange !== curr?.filterRange ||
            prev?.filterCampaign !== curr?.filterCampaign ||
            prev?.filterStatus !== curr?.filterStatus ||
            prev?.filterDate !== curr?.filterDate ||
            prev?.filterOrganisation !== curr?.filterOrganisation ||
            prev?.filterMember !== curr?.filterMember ||
            prev?.filterBatchId !== curr?.filterBatchId ||
            prev?.filterGtin !== curr?.filterGtin ||
            prev?.maxRows !== curr?.maxRows

        const pageChanged = prev?.page !== curr?.page

        if (otherFiltersChanged && curr?.page !== 1) {
            setDataTableFirst(1)
            setFilters((prev: any) => ({...prev, page: 1}))
        } else if (pageChanged || otherFiltersChanged) {
            if (!preloading && filters) refreshProducts()
        }

        prevFiltersRef.current = filters
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    // Filtering ranges & brands on refresh
    useEffect(() => {
        if (!!ranges && affectedRanges === null) setAffectedRanges(ranges)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ranges])

    useEffect(() => {
        if (!!filters?.filterBrand) {
            let _rangesInBrand = ranges.filter((range: any) => range.brand.id === filters?.filterBrand)
            setAffectedRanges(_rangesInBrand)
        } else setAffectedRanges(ranges)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters?.filterBrand])

    // Empty message dynamic refresh
    useEffect(() => {
        if (dataTableEmptyMessage === "common:loading") {
            setTimeout(() => setDataTableEmptyMessage("no_products"), 4000)
        }
    }, [dataTableEmptyMessage]);

    // Hydrating organisation filter on change
    useEffect(() => {
        if (!!filters && commonFilters.organisation !== filters?.filterOrganisation) {
            handleChangeFilters("filterOrganisation", commonFilters?.organisation)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonFilters]);

    // Filters
    const hydrateFilters = async () => {
        const _filters = new Map(userSettings.packaging_filter.columns.map((column: any) => [column.column_name, column.value]))
        const _filtersObject = await Object.fromEntries(_filters)

        const _tmpFilters = {
            maxRows: userSettings.packaging_filter.max_rows,
            page: userSettings.packaging_filter.page,
            lineHeight: userSettings.packaging_filter.line_height,
            ..._filtersObject
        }

        setFilters(_tmpFilters)
        setDataTableFirst((_tmpFilters.page * _tmpFilters.maxRows) - _tmpFilters.maxRows)

        const _columns = new Map(userSettings.packaging_filter.columns.map((column: any) => [column.column_name, column.active]))
        const _columnsObject = await Object.fromEntries(_columns)

        setColumns(_columnsObject)

        // specific case for dates
        let _date = _filters.get("filterDate")
        if (!!_date) {
            setDisplayedDate(_date as Date)
        }
    }

    const handleChangeFilters = (filter: string, value: any) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            let _filters = {
                ...filters,
                [filter]: value
            }

            setFilters(_filters)

            savePackagingFilters(formatFiltersBody(_filters, columns))
        }, 300)
    }

    // applyFilters commits a batch of draft filter fields at once. Used by
    // the Search button, which stages input in ProductFilters' local state
    // and only pushes to the real filters object on click. Bypasses the
    // 300ms debounce (which exists to coalesce per-keystroke writes — no
    // longer needed once the fields are deferred) so a single Search click
    // produces one refreshProducts call, not zero because the last-timer
    // overwrite pattern in handleChangeFilters would drop all but one field.
    const applyFilters = (draft: Partial<typeof initFilters>) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
        const _filters = {...filters, ...draft, page: 1}
        setFilters(_filters)
        savePackagingFilters(formatFiltersBody(_filters, columns))
    }

    const handleChangeDisplayedColumns = (column: any) => {

        let _columns = {
            ...columns,
            [column]: !columns[column]
        }

        setColumns(_columns)

        savePackagingFilters(formatFiltersBody(filters, _columns))
    }

    const clearFilters = () => {
        setFilters(initFilters)
        setDisplayedDate(null)
        clearCommonFilters()
        savePackagingFilters(formatFiltersBody(initFilters, columns))
    }

    // Products
    const refreshProducts = () => {
        if (!preloading) {
            setPreloading(true)
            setLoading(true)
            setDataTableEmptyMessage("common:loading");
            getProducts(getQueryUrl(filters)).finally(() => {
                setPreloading(false)
                setLoading(false)
            }).catch(() => setLoading(false))
        }
    }

    // Pagination
    const onPageChange = (e: PageEvent) => {
        if (!!e.rows && e.rows !== filters.maxRows) {
            handleChangeFilters("maxRows", e.rows);
            return
        } else if (e.page !== undefined) {
            setDataTableFirst(e.first);
            handleChangeFilters("page", e.page + 1);
        }
    }

    // Analytics
    const seeInAnalytics = (rowData?: any) => {
        if (!!multipleSelection && multipleSelection.length > 0) {
            const payload: any = ["analytics-uuids"]

            multipleSelection.forEach((product: any) => {
                payload.push({ label: product.name, uuid: product.id })
            })

            seeAnalyticsForProducts(payload)
        } else if (!!rowData) {
            const payload = ["analytics-uuids", { label: rowData.name, uuid: rowData.id }]
            seeAnalyticsForProducts(payload)
        } else return
    }

    const canCreate = !isAccountRole(["analyst"])

    // Batch import moved to the Batch management page (Tools).

    return (
        <Box>
            <PageHeader
                title={t("products")}
                subtitle={t("products_subtitle")}
                actions={
                    <>
                        <Button
                            component={Link}
                            to={"/products/gs1/create"}
                            data-step-target={"create-product-gs1"}
                            disabled={!modules || !modules.gs1 || !canCreate}
                            startIcon={<Add sx={{fontSize: 16}} />}
                            sx={secondaryBtnSx}
                        >
                            {t("product_gs1_create")}
                        </Button>
                        <Button
                            variant={"contained"}
                            disableElevation
                            component={Link}
                            to={"/products/create"}
                            data-step-target={"create-product"}
                            disabled={!modules || !modules.geoloc || !canCreate}
                            startIcon={<Add sx={{fontSize: 16}} />}
                            sx={primaryBtnSx}
                        >
                            {t("product_create")}
                        </Button>
                    </>
                }
            />

            <ProductFilters
                gs1={!!modules && modules.gs1}
                {...{
                    modules,
                    brands,
                    affectedRanges,
                    showFilters, setShowFilters,
                    clearFilters,
                    isFiltered,
                    filters, applyFilters,
                    t
                }}
            />

            <ProductsDataTable
                disableActions={isAccountRole(["analyst"])}
                gs1={!!modules && modules.gs1}
                {...{
                    loading,
                    dataTableEmptyMessage,
                    filters,
                    dataTableFirst,
                    onPageChange,
                    products,
                    selection, setSelection,
                    multipleSelection, setMultipleSelection,
                    seeInAnalytics, columns,
                    handleChangeDisplayedColumns,
                    handleChangeFilters,
                    t
                }}
            />
        </Box>
    )
}

// Shared header-button styles (Rémi). Kept local to this tab for now; a shared
// Button wrapper is a later consolidation step.
const primaryBtnSx = {
    textTransform: "none",
    fontSize: "13.5px",
    fontWeight: 600,
    borderRadius: tk.radius.sm,
    px: 2,
}
const secondaryBtnSx = {
    textTransform: "none",
    fontSize: "13.5px",
    fontWeight: 600,
    borderRadius: tk.radius.sm,
    px: 2,
    color: tk.color.textPrimary,
    border: `1px solid ${tk.color.border}`,
    backgroundColor: tk.color.surface,
    "&:hover": {backgroundColor: tk.color.bg, borderColor: tk.color.borderStrong},
}
